/**
 * Fachgespräch-Modus — pure Logik: Kartenwahl + Sokrates-Prompt.
 *
 * Prompt-Wortlaut folgt tools/prompts/voice_fachgespraech.md (Sokrates-Template
 * für externe Voice-KIs) — hier pro Karte parametrisiert zum Kopieren.
 */

import { shuffle } from '$lib/exam';
import type { Card } from '$types/card';

/** Bevorzugt Karten mit self_explain (Bisra-Pflichtkarten), sonst qa/calc/cloze. */
export function pickExplainCard(
  cards: readonly Card[],
  rng: () => number = Math.random,
): Card | null {
  const withExplain = cards.filter((c) => c.self_explain);
  const pool = withExplain.length
    ? withExplain
    : cards.filter((c) => c.type === 'qa' || c.type === 'calc' || c.type === 'cloze');
  return shuffle(pool, rng)[0] ?? null;
}

function cardTopic(card: Card): string {
  if ('front' in card && card.front) return card.front;
  if ('text' in card && card.text) return card.text.replace(/\{\{c\d+::(.+?)\}\}/g, '$1');
  return card.id;
}

export function sokratesPrompt(card: Card): string {
  const rubric = card.self_explain?.rubric?.length
    ? `\nPRÜF-SCHWERPUNKTE (müssen vorkommen): ${card.self_explain.rubric.join(', ')}`
    : '';
  return `Du bist Prüfer in der Anlagenmechaniker-SHK-Gesellenprüfung Teil 2 (HWK Köln, Dezember 2026). Heute ist das Fachgespräch.

DEINE ROLLE:
- Freundlich, aber prüfungsstreng. Sokrates-Methode: Du stellst Fragen, gibst keine direkten Antworten. Wenn ich falsch liege, fragst du nach Begründung (statt zu korrigieren).
- Du bewertest am Ende in 3 Punkten: 1. Fachliche Tiefe, 2. Argumentations-Struktur, 3. DIN-/Norm-Bezug.

THEMA HEUTE: ${cardTopic(card).trim()}
${card.self_explain?.prompt ? `\nEINSTIEG: ${card.self_explain.prompt.trim()}` : ''}${rubric}

REGEL:
- Frage ZUERST nach meiner Erklärung des Themas.
- WARTE auf meine Antwort, BEVOR du den nächsten Aspekt anschneidest.
- Wenn ich stocke, gib einen MINIMAL-Hinweis (nicht die Antwort).
- Verwende deutsche Fachbegriffe konsequent (TRGI 2018, DIN 1988-200, GEG etc.).

START: Stell mir die erste Frage.`;
}
